import { setCookie } from 'v2/compat/login-utils'
import { useAuth } from 'v2/shared/hooks/use-auth'
import type { RLoginResponse, RUserSession } from 'v2/types/api/auth'
import { AnalyticsEvents, track } from 'v2/utils/analytics'

/**
 * Creates a user session object from login response data
 */
export const createUserSession = (loginResponse: RLoginResponse, email?: string): RUserSession => ({
  auth_token: loginResponse.auth_token,
  aom_uuid: loginResponse.aom_uuid,
  browser_session_id: loginResponse.browser_session_id || '',
  organization_name: loginResponse.organization_name || '',
  // No `|| ''` fallback here (unlike the fields above): undefined is a meaningful,
  // distinct state from '' for this one — it's how cli-authorize.tsx tells "backend
  // didn't send a uuid yet, fall back to name matching" apart from a real uuid.
  organization_uuid: loginResponse.organization_uuid,
  region: loginResponse.region || '',
  roles: loginResponse.roles,
  email: loginResponse.email || email || '',
  name: loginResponse.name || '',
})

/**
 * Sets authentication details in cookie
 */
export const setAuthCookie = (loginResponse: RLoginResponse) => {
  setCookie(
    'aut',
    JSON.stringify({
      auth_token: loginResponse.auth_token,
      region: loginResponse.region || '',
      session_id: loginResponse.browser_session_id || '',
      aom_uuid: loginResponse.aom_uuid || '',
      roles: loginResponse.roles,
      orgName: loginResponse.organization_name || '',
    }),
  )
}

/**
 * Custom hook that provides session management utilities
 */
export const useSessionManager = () => {
  const auth = useAuth()

  const completeLoginProcess = (loginResponse: RLoginResponse, email?: string) => {
    const userSession = createUserSession(loginResponse, email)
    auth.loginSuccess(userSession)
    setAuthCookie(loginResponse)
    // Person identity by email lands via useIdentifyAnalytics once the app
    // mounts; here we just mark the session start. Best-effort.
    track(AnalyticsEvents.USER_LOGGED_IN, { region: loginResponse.region || '' })
  }

  return {
    completeLoginProcess,
    createUserSession,
    setAuthCookie,
  }
}
